import { apiRequest } from '../client'
import type { ProductPrice } from '../types'

export const pricesApi = {
  list: (sku: string) =>
    apiRequest<{ items: Array<ProductPrice> }>(
      `/api/v0/admin/products/${encodeURIComponent(sku)}/prices`,
    ),

  get: (sku: string, restaurantId: number) =>
    apiRequest<ProductPrice>(
      `/api/v0/admin/products/${encodeURIComponent(sku)}/prices?restaurantId=${restaurantId}`,
    ),

  set: (sku: string, restaurantId: number, amountCents: number) =>
    apiRequest<{ ok: boolean; updated: number }>(
      `/api/v0/admin/products/${encodeURIComponent(sku)}/prices/${restaurantId}`,
      {
        method: 'PUT',
        body: JSON.stringify({ amountCents }),
      },
    ),

  bulkSetAll: (sku: string, amountCents: number) =>
    apiRequest<{ ok: boolean; summary: Record<string, number> }>(
      `/api/v0/admin/products/${encodeURIComponent(sku)}/prices/bulk-set-all`,
      {
        method: 'POST',
        body: JSON.stringify({ amountCents }),
      },
    ),

  // Client-side bulk update with Promise.allSettled
  bulkUpdate: async (
    updates: Array<{ sku: string; restaurantId: number; amountCents: number }>,
  ) => {
    const results = await Promise.allSettled(
      updates.map(({ sku, restaurantId, amountCents }) =>
        apiRequest(
          `/api/v0/admin/products/${encodeURIComponent(sku)}/prices/${restaurantId}`,
          {
            method: 'PUT',
            body: JSON.stringify({ amountCents }),
          },
        ),
      ),
    )

    const succeeded = results.filter((r) => r.status === 'fulfilled').length
    const failed = results.filter((r) => r.status === 'rejected').length

    return { succeeded, failed }
  },

  matrix: (params: {
    search?: string
    page?: number
    size?: number
    includeInactive?: boolean
    restaurantIds?: Array<number>
  }) => {
    const sp = new URLSearchParams()
    if (params.search) sp.set('search', params.search)
    if (params.page !== undefined) sp.set('page', String(params.page))
    if (params.size !== undefined) sp.set('size', String(params.size))
    if (params.includeInactive) sp.set('includeInactive', 'true')
    if (params.restaurantIds && params.restaurantIds.length > 0) {
      sp.set('restaurantIds', params.restaurantIds.join(','))
    }
    const qs = sp.toString()
    const url = `/api/v0/admin/prices/matrix${qs ? `?${qs}` : ''}`
    return apiRequest<{
      restaurants: Array<{ id: number; code: string; name: string }>
      items: Array<{
        sku: string
        name: string
        vatCode: string
        active: boolean
        prices: Array<{ restaurantId: number; amountCents?: number }>
      }>
      page: number
      size: number
      total: number
    }>(url)
  },
}
